import { useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import { formatDisplayTime } from "./timeHelpers";

function TimerPictureInPictureView({ label, timerSeconds, theme }) {
  const colors = theme?.colors || {};

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: colors.background || "#111827",
        color: colors.text || "#f9fafb",
        fontFamily: theme?.fontFamily || "system-ui, sans-serif",
        padding: "24px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "360px",
          textAlign: "center",
          border: `1px solid ${colors.logoBackground || colors.text || "#374151"}`,
          borderRadius: "20px",
          padding: "24px 20px",
          background: "rgba(255, 255, 255, 0.04)",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            textTransform: "uppercase",
            letterSpacing: "0.14em",
            fontSize: "13px",
            marginBottom: "12px",
            opacity: 0.9,
          }}
        >
          {label}
        </div>
        <div
          style={{
            color: colors.timerText || colors.logoBackground || colors.text || "#f9fafb",
            fontSize: "clamp(42px, 18vw, 76px)",
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {formatDisplayTime(timerSeconds)}
        </div>
      </div>
    </div>
  );
}

function getDocumentPictureInPicture() {
  if (typeof window === "undefined") {
    return null;
  }

  return window.documentPictureInPicture || null;
}

export default function useTimerPictureInPicture({
  running,
  timerSeconds,
  label,
  theme,
}) {
  const pipWindowRef = useRef(null);
  const pipRootRef = useRef(null);
  const closeHandlerRef = useRef(null);
  const requestInFlightRef = useRef(false);
  const latestStateRef = useRef({ label, timerSeconds, theme });

  useEffect(() => {
    latestStateRef.current = { label, timerSeconds, theme };
  }, [label, theme, timerSeconds]);

  useEffect(() => {
    const pipWindow = pipWindowRef.current;

    if (!pipWindow || pipWindow.closed || !pipRootRef.current) {
      return;
    }

    pipWindow.document.title = label || "Timer";
    pipRootRef.current.render(
      <TimerPictureInPictureView
        label={label}
        timerSeconds={timerSeconds}
        theme={theme}
      />
    );
  }, [label, theme, timerSeconds]);

  useEffect(() => {
    function clearPiPReferences() {
      if (closeHandlerRef.current && pipWindowRef.current) {
        pipWindowRef.current.removeEventListener("pagehide", closeHandlerRef.current);
      }

      if (pipRootRef.current) {
        pipRootRef.current.unmount();
      }

      closeHandlerRef.current = null;
      pipRootRef.current = null;
      pipWindowRef.current = null;
      requestInFlightRef.current = false;
    }

    function closePiP() {
      const pipWindow = pipWindowRef.current;

      if (!pipWindow) {
        requestInFlightRef.current = false;
        return;
      }

      clearPiPReferences();

      if (!pipWindow.closed) {
        try {
          pipWindow.close();
        } catch {
          // Ignore close failures and preserve existing timer behavior.
        }
      }
    }

    async function openPiP() {
      if (!running || requestInFlightRef.current || pipWindowRef.current) {
        return;
      }

      const documentPiP = getDocumentPictureInPicture();

      if (!documentPiP?.requestWindow) {
        return;
      }

      requestInFlightRef.current = true;

      try {
        const pipWindow = await documentPiP.requestWindow({ width: 360, height: 220 });
        const container = pipWindow.document.createElement("div");
        const currentState = latestStateRef.current;

        pipWindow.document.body.innerHTML = "";
        pipWindow.document.body.style.margin = "0";
        pipWindow.document.body.style.minHeight = "100vh";
        pipWindow.document.body.appendChild(container);

        pipWindowRef.current = pipWindow;
        pipRootRef.current = createRoot(container);

        const handlePiPClose = () => {
          clearPiPReferences();
        };

        closeHandlerRef.current = handlePiPClose;
        pipWindow.addEventListener("pagehide", handlePiPClose, { once: true });

        pipRootRef.current.render(
          <TimerPictureInPictureView
            label={currentState.label}
            timerSeconds={currentState.timerSeconds}
            theme={currentState.theme}
          />
        );
      } catch {
        requestInFlightRef.current = false;
      } finally {
        if (pipWindowRef.current) {
          requestInFlightRef.current = false;
        }
      }
    }

    function handleVisibilityChange() {
      if (!document.hidden || !running) {
        closePiP();
        return;
      }

      void openPiP();
    }

    if (!getDocumentPictureInPicture()) {
      return undefined;
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);

    if (!running) {
      closePiP();
    } else if (document.hidden) {
      void openPiP();
    }

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      closePiP();
    };
  }, [running]);
}
